/**
 * Decodificación de audio + cálculo de picos para visualización.
 *
 * Cacheamos AudioBuffer por URL (en memoria — válido durante la sesión).
 */

const _bufferCache = new Map<string, Promise<AudioBuffer>>();
let _audioCtx: AudioContext | null = null;

function audioContext(): AudioContext {
  if (!_audioCtx) {
    _audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return _audioCtx;
}

export function fetchAudioBuffer(url: string): Promise<AudioBuffer> {
  let p = _bufferCache.get(url);
  if (!p) {
    p = (async () => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`Audio fetch failed: ${url}`);
      const buf = await r.arrayBuffer();
      // decodeAudioData devuelve un AudioBuffer
      return await audioContext().decodeAudioData(buf);
    })();
    _bufferCache.set(url, p);
    // si falla, no cacheamos el error
    p.catch(() => _bufferCache.delete(url));
  }
  return p;
}

/**
 * Calcula picos (min/max) por bucket de longitud `samplesPerBucket`.
 * Devuelve dos arrays paralelos. Tamaño = numBuckets.
 */
export function computePeaks(
  buffer: AudioBuffer,
  startSec: number,
  endSec: number,
  numBuckets: number,
): { mins: Float32Array; maxs: Float32Array } {
  const sampleRate = buffer.sampleRate;
  const startSample = Math.max(0, Math.floor(startSec * sampleRate));
  const endSample = Math.min(buffer.length, Math.ceil(endSec * sampleRate));
  const totalSamples = Math.max(1, endSample - startSample);
  const samplesPerBucket = totalSamples / numBuckets;

  // Mezcla mono si stereo
  const ch0 = buffer.getChannelData(0);
  const ch1 = buffer.numberOfChannels > 1 ? buffer.getChannelData(1) : null;

  const mins = new Float32Array(numBuckets);
  const maxs = new Float32Array(numBuckets);

  for (let i = 0; i < numBuckets; i++) {
    const s = startSample + Math.floor(i * samplesPerBucket);
    const e = startSample + Math.min(endSample, Math.floor((i + 1) * samplesPerBucket));
    let mn = 1, mx = -1;
    for (let j = s; j < e; j++) {
      let v = ch0[j];
      if (ch1) v = (v + ch1[j]) * 0.5;
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    mins[i] = mn;
    maxs[i] = mx;
  }
  return { mins, maxs };
}
